const HAND_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8],
  [5, 9], [9, 10], [10, 11], [11, 12],
  [9, 13], [13, 14], [14, 15], [15, 16],
  [13, 17], [17, 18], [18, 19], [19, 20],
  [0, 17],
];

/**
 * UI controller for the particle field and opt-in camera preview.
 * DOM controls emit semantic events and never touch Three.js objects directly.
 */
export class UI {
  constructor(root) {
    this.root = root;
    this.listeners = new Map();
    this.controlHandlers = [];
    this.colorHandlers = [];
    this.cameraRequested = false;
    this.cameraState = "off";
    this.lastInteractionMode = "idle";
    this.activeColorSlot = "primary";
    this.elements = {
      cameraPlaceholder: root.querySelector('[data-ui="camera-placeholder"]'),
      cameraPreview: root.querySelector('[data-ui="camera-preview"]'),
      cameraToggle: root.querySelector('[data-ui="camera-toggle"]'),
      cameraVideo: root.querySelector('[data-ui="camera-video"]'),
      colorConsole: root.querySelector('[data-ui="color-console"]'),
      colorModes: Array.from(
        root.querySelectorAll('[data-ui="color-mode"]'),
      ),
      colorPrimary: root.querySelector('[data-ui="color-primary"]'),
      colorPrimaryValue: root.querySelector(
        '[data-ui="color-primary-value"]',
      ),
      colorSecondary: root.querySelector('[data-ui="color-secondary"]'),
      colorSecondaryValue: root.querySelector(
        '[data-ui="color-secondary-value"]',
      ),
      colorWellPrimary: root.querySelector(
        '[data-ui="color-well-primary"]',
      ),
      colorWellSecondary: root.querySelector(
        '[data-ui="color-well-secondary"]',
      ),
      density: root.querySelector('[data-ui="density"]'),
      densityOutput: root.querySelector('[data-ui="density-output"]'),
      error: root.querySelector('[data-ui="error"]'),
      fps: root.querySelector('[data-ui="fps"]'),
      fullscreen: root.querySelector('[data-ui="fullscreen"]'),
      gestureStatus: root.querySelector('[data-ui="gesture-status"]'),
      glow: root.querySelector('[data-ui="glow"]'),
      glowOutput: root.querySelector('[data-ui="glow-output"]'),
      handedness: root.querySelector('[data-ui="handedness"]'),
      handOverlay: root.querySelector('[data-ui="hand-overlay"]'),
      inference: root.querySelector('[data-ui="inference"]'),
      landmarkCount: root.querySelector('[data-ui="landmark-count"]'),
      model: root.querySelector('[data-ui="model"]'),
      modelCaption: root.querySelector('[data-ui="model-caption"]'),
      particleSize: root.querySelector('[data-ui="particle-size"]'),
      particleSizeOutput: root.querySelector(
        '[data-ui="particle-size-output"]',
      ),
      quality: root.querySelector('[data-ui="quality"]'),
      rgbInputs: Array.from(root.querySelectorAll('[data-ui="rgb-input"]')),
      rgbOutputs: {
        r: root.querySelector('[data-ui="rgb-r-output"]'),
        g: root.querySelector('[data-ui="rgb-g-output"]'),
        b: root.querySelector('[data-ui="rgb-b-output"]'),
      },
      rotationSensitivity: root.querySelector(
        '[data-ui="rotation-sensitivity"]',
      ),
      rotationSensitivityOutput: root.querySelector(
        '[data-ui="rotation-sensitivity-output"]',
      ),
      status: root.querySelector('[data-ui="status"]'),
      statusLight: root.querySelector('[data-ui="status-light"]'),
    };

    this.handleFullscreenClick = this.toggleFullscreen.bind(this);
    this.handleFullscreenChange = this.syncFullscreenButton.bind(this);
    this.handleCameraClick = () => {
      this.cameraRequested = !this.cameraRequested;
      this.emit("cameraToggle", this.cameraRequested);
    };
    this.handleModelChange = () => {
      const selected = this.elements.model.selectedOptions[0];
      this.emit("modelChange", {
        id: this.elements.model.value,
        label: selected.textContent,
      });
    };

    this.elements.cameraToggle.addEventListener(
      "click",
      this.handleCameraClick,
    );
    this.elements.fullscreen.addEventListener(
      "click",
      this.handleFullscreenClick,
    );
    document.addEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
    this.elements.model.addEventListener("change", this.handleModelChange);

    this.bindRangeControl(
      this.elements.density,
      this.elements.densityOutput,
      "densityChange",
      (value) => Math.round(value / 1000) + "K",
    );
    this.bindRangeControl(
      this.elements.particleSize,
      this.elements.particleSizeOutput,
      "particleSizeChange",
      (value) => value.toFixed(1),
    );
    this.bindRangeControl(
      this.elements.glow,
      this.elements.glowOutput,
      "glowChange",
      (value) => value.toFixed(2),
    );
    this.bindRangeControl(
      this.elements.rotationSensitivity,
      this.elements.rotationSensitivityOutput,
      "rotationSensitivityChange",
      (value) => Math.round(value * 100) + "%",
    );
    this.bindColorControls();
  }

  bindColorControls() {
    this.colorMode =
      this.elements.colorModes.find((input) => input.checked)?.value ??
      "gradient";
    this.palette = {
      primary: normalizeHex(this.elements.colorPrimary.value),
      secondary: normalizeHex(this.elements.colorSecondary.value),
    };

    const bind = (element, eventName, handler) => {
      element.addEventListener(eventName, handler);
      this.colorHandlers.push({ element, eventName, handler });
    };

    [
      ["primary", this.elements.colorPrimary],
      ["secondary", this.elements.colorSecondary],
    ].forEach(([slot, input]) => {
      const activate = () => this.setActiveColorSlot(slot);
      const update = () => {
        this.setActiveColorSlot(slot);
        this.palette[slot] = normalizeHex(input.value);
        this.updatePalettePresentation();
        this.emitColorChange();
      };
      bind(input, "focus", activate);
      bind(input, "click", activate);
      bind(input, "input", update);
    });

    this.elements.colorModes.forEach((input) => {
      bind(input, "change", () => {
        if (!input.checked) {
          return;
        }
        this.colorMode = input.value;
        if (this.colorMode === "single") {
          this.activeColorSlot = "primary";
        }
        this.updatePalettePresentation();
        this.emitColorChange();
      });
    });

    this.elements.rgbInputs.forEach((input) => {
      bind(input, "input", () => {
        const rgb = hexToRgb(this.palette[this.activeColorSlot]);
        rgb[input.dataset.channel] = Number.parseInt(input.value, 10);
        this.palette[this.activeColorSlot] = rgbToHex(rgb.r, rgb.g, rgb.b);
        this.updatePalettePresentation();
        this.emitColorChange();
      });
    });

    this.updatePalettePresentation();
  }

  setActiveColorSlot(slot) {
    if (slot === "secondary" && this.colorMode === "single") {
      return;
    }
    this.activeColorSlot = slot;
    this.updatePalettePresentation();
  }

  setPalette(
    primary,
    secondary,
    { mode = this.colorMode, emit = false } = {},
  ) {
    this.palette.primary = normalizeHex(primary);
    this.palette.secondary = normalizeHex(secondary ?? primary);
    this.colorMode = mode === "single" ? "single" : "gradient";
    if (this.colorMode === "single") {
      this.activeColorSlot = "primary";
    }
    this.updatePalettePresentation();
    if (emit) {
      this.emitColorChange();
    }
  }

  getPaletteState() {
    return {
      mode: this.colorMode,
      primary: this.palette.primary,
      secondary: this.palette.secondary,
    };
  }

  emitColorChange() {
    this.emit("colorChange", this.getPaletteState());
  }

  updatePalettePresentation() {
    const { primary, secondary } = this.palette;
    this.elements.colorPrimary.value = primary;
    this.elements.colorSecondary.value = secondary;
    this.elements.colorPrimaryValue.textContent = primary.toUpperCase();
    this.elements.colorSecondaryValue.textContent = secondary.toUpperCase();
    this.elements.colorConsole.dataset.mode = this.colorMode;
    this.elements.colorSecondary.disabled = this.colorMode === "single";
    this.elements.colorWellPrimary.dataset.active = String(
      this.activeColorSlot === "primary",
    );
    this.elements.colorWellSecondary.dataset.active = String(
      this.activeColorSlot === "secondary" && this.colorMode === "gradient",
    );
    this.elements.colorModes.forEach((input) => {
      input.checked = input.value === this.colorMode;
    });
    this.root.style.setProperty("--spectrum-a", primary);
    this.root.style.setProperty(
      "--spectrum-b",
      this.colorMode === "single" ? primary : secondary,
    );

    const activeRgb = hexToRgb(this.palette[this.activeColorSlot]);
    this.elements.rgbInputs.forEach((input) => {
      const value = activeRgb[input.dataset.channel];
      input.value = String(value);
      this.elements.rgbOutputs[input.dataset.channel].value = String(value);
      this.elements.rgbOutputs[input.dataset.channel].textContent = String(value);
    });
  }

  bindRangeControl(input, output, eventName, formatter) {
    const handler = () => {
      const value = Number.parseFloat(input.value);
      output.value = formatter(value);
      output.textContent = formatter(value);
      this.emit(eventName, value);
    };

    input.addEventListener("input", handler, { passive: true });
    this.controlHandlers.push({ input, handler });
    handler();
  }

  on(eventName, listener) {
    const listeners = this.listeners.get(eventName) ?? new Set();
    listeners.add(listener);
    this.listeners.set(eventName, listeners);
    return () => listeners.delete(listener);
  }

  emit(eventName, detail) {
    this.listeners.get(eventName)?.forEach((listener) => listener(detail));
  }

  reveal() {
    requestAnimationFrame(() => this.root.classList.add("is-ready"));
  }

  setFps(value) {
    this.elements.fps.textContent = String(Math.round(value));
  }

  setQuality(label) {
    this.elements.quality.textContent = label;
  }

  setStatus(label, state = "online") {
    this.elements.status.textContent = label;
    this.elements.statusLight.dataset.state = state;
  }

  setModelState(label, state = "online") {
    const status =
      state === "morphing" ? "Morphing · " + label : label + " field online";
    this.setStatus(status, state);
    this.elements.modelCaption.textContent =
      label + (state === "morphing" ? " / Reconstructing" : " / Morph field active");
  }

  setCameraState(state, message) {
    const stateLabels = {
      off: "Off",
      loading: "Preparing",
      searching: "Show one hand",
      tracking: "Tracking",
      error: "Unavailable",
    };
    const isRequested = state !== "off" && state !== "error";
    const hasVideo = state === "searching" || state === "tracking";

    this.cameraState = state;
    this.cameraRequested = isRequested;
    this.elements.cameraToggle.dataset.state = state;
    this.elements.cameraPreview.dataset.state = state;
    this.elements.cameraPreview.hidden = state === "off";
    this.elements.cameraPlaceholder.hidden = hasVideo;
    this.elements.gestureStatus.textContent = message ?? stateLabels[state];

    const buttonLabel = isRequested
      ? "关闭手势摄像头"
      : "启用手势摄像头";
    this.elements.cameraToggle.setAttribute("aria-label", buttonLabel);
    this.elements.cameraToggle.setAttribute("title", buttonLabel);

    if (state === "off" || state === "error") {
      this.lastInteractionMode = "idle";
      this.elements.cameraPreview.dataset.gesture = "idle";
      this.clearHandOverlay();
      this.elements.landmarkCount.textContent = "0 / 21";
      this.elements.handedness.textContent = "--";
      this.elements.inference.textContent = "-- ms";
    }
  }

  updateHandFrame(frame) {
    if (frame.status === "error") {
      this.setCameraState("error", cameraErrorLabel(frame.error));
      return;
    }

    if (frame.detected) {
      this.setCameraState(
        "tracking",
        interactionModeLabel(this.lastInteractionMode),
      );
      this.elements.landmarkCount.textContent =
        frame.landmarks.length + " / 21";
      this.elements.handedness.textContent =
        frame.handedness ?? "Unknown";
      this.elements.inference.textContent =
        frame.inferenceMs.toFixed(1) + " ms";
      this.drawHandOverlay(frame);
      return;
    }

    if (frame.status === "searching") {
      this.setCameraState("searching", "Show one hand");
      this.elements.inference.textContent =
        frame.inferenceMs > 0 ? frame.inferenceMs.toFixed(1) + " ms" : "-- ms";
      this.clearHandOverlay();
    }
  }

  updateInteractionState(interaction) {
    if (
      !interaction ||
      interaction.mode === this.lastInteractionMode
    ) {
      return;
    }

    this.lastInteractionMode = interaction.mode;
    this.elements.cameraPreview.dataset.gesture = interaction.mode;
    if (this.cameraState === "tracking") {
      this.elements.gestureStatus.textContent =
        interactionModeLabel(interaction.mode);
    }
  }

  getCameraVideo() {
    return this.elements.cameraVideo;
  }

  drawHandOverlay(frame) {
    const canvas = this.elements.handOverlay;
    const bounds = canvas.getBoundingClientRect();
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    const width = Math.max(1, Math.round(bounds.width * pixelRatio));
    const height = Math.max(1, Math.round(bounds.height * pixelRatio));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const context = canvas.getContext("2d");
    context.clearRect(0, 0, width, height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 1.15 * pixelRatio;
    context.strokeStyle = "rgba(115, 217, 255, 0.68)";
    context.shadowColor = "rgba(115, 217, 255, 0.42)";
    context.shadowBlur = 5 * pixelRatio;

    context.beginPath();
    HAND_CONNECTIONS.forEach(([from, to]) => {
      const start = frame.landmarks[from];
      const end = frame.landmarks[to];
      context.moveTo(start.x * width, start.y * height);
      context.lineTo(end.x * width, end.y * height);
    });
    context.stroke();

    context.shadowBlur = 8 * pixelRatio;
    frame.landmarks.forEach((point, index) => {
      const isTip = [4, 8, 12, 16, 20].includes(index);
      context.beginPath();
      context.arc(
        point.x * width,
        point.y * height,
        (isTip ? 2.2 : 1.35) * pixelRatio,
        0,
        Math.PI * 2,
      );
      context.fillStyle = isTip
        ? "rgba(234, 246, 255, 0.95)"
        : "rgba(115, 217, 255, 0.78)";
      context.fill();
    });

    context.beginPath();
    context.arc(
      frame.palm.x * width,
      frame.palm.y * height,
      Math.max(5, frame.palm.size * width * 0.24),
      0,
      Math.PI * 2,
    );
    context.strokeStyle = "rgba(168, 139, 255, 0.72)";
    context.lineWidth = 1 * pixelRatio;
    context.stroke();
  }

  clearHandOverlay() {
    const canvas = this.elements.handOverlay;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
  }

  showWebGLError() {
    this.root.classList.add("has-renderer-error");
    this.elements.error.hidden = false;
    this.setStatus("Renderer unavailable", "error");
    this.setQuality("Unavailable");
  }

  async toggleFullscreen() {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
      this.emit("fullscreenChange", Boolean(document.fullscreenElement));
    } catch {
      this.setStatus("Fullscreen blocked", "warning");
    }
  }

  syncFullscreenButton() {
    const isFullscreen = Boolean(document.fullscreenElement);
    const label = isFullscreen ? "退出全屏" : "进入全屏";
    this.elements.fullscreen.setAttribute("aria-label", label);
    this.elements.fullscreen.setAttribute("title", label);
    this.elements.fullscreen.classList.toggle("is-active", isFullscreen);
  }

  dispose() {
    this.elements.cameraToggle.removeEventListener(
      "click",
      this.handleCameraClick,
    );
    this.elements.fullscreen.removeEventListener(
      "click",
      this.handleFullscreenClick,
    );
    document.removeEventListener(
      "fullscreenchange",
      this.handleFullscreenChange,
    );
    this.elements.model.removeEventListener("change", this.handleModelChange);
    this.controlHandlers.forEach(({ input, handler }) => {
      input.removeEventListener("input", handler);
    });
    this.colorHandlers.forEach(({ element, eventName, handler }) => {
      element.removeEventListener(eventName, handler);
    });
    this.controlHandlers.length = 0;
    this.colorHandlers.length = 0;
    this.listeners.clear();
  }
}

export function hexToRgb(hex) {
  if (typeof hex !== "string") {
    throw new TypeError("Color must be a hexadecimal string.");
  }
  const compact = hex.trim().replace(/^#/, "");
  const expanded = compact.length === 3
    ? compact.split("").map((value) => value + value).join("")
    : compact;
  if (!/^[0-9a-f]{6}$/i.test(expanded)) {
    throw new RangeError("Color must use #RGB or #RRGGBB format.");
  }
  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}

export function rgbToHex(red, green, blue) {
  const channel = (value) => Math.max(
    0,
    Math.min(255, Math.round(Number.isFinite(value) ? value : 0)),
  ).toString(16).padStart(2, "0");
  return "#" + channel(red) + channel(green) + channel(blue);
}

function normalizeHex(color) {
  const { r, g, b } = hexToRgb(color);
  return rgbToHex(r, g, b);
}

function cameraErrorLabel(code) {
  const labels = {
    "permission-denied": "Permission denied",
    "camera-unavailable": "No camera",
    "camera-busy": "Camera busy",
    "camera-constraints": "Unsupported camera",
    "insecure-context": "HTTPS required",
    "model-load-failed": "Model unavailable",
    "inference-failed": "Tracking paused",
  };
  return labels[code] ?? "Camera unavailable";
}

function interactionModeLabel(mode) {
  const labels = {
    idle: "Show one hand",
    neutral: "Hand online",
    open: "Open · Expand",
    grabbed: "Gravity lock",
    palming: "Palm · Rotate",
  };
  return labels[mode] ?? "Tracking";
}
